import * as XLSX from "xlsx-js-style";
import moment from "moment-timezone";
import {
  amountCell,
  dateCell,
  FOOTER_STYLE,
  numberCell,
  TABLE_HEADER_STYLE,
  textCell,
  TITLE_STYLE,
  TOTAL_STYLE,
  withStyle,
  WRAP_STYLE,
} from "./auctionExcelExport";
import {
  auctionFormatText,
  auctionTypeText,
  DATE_TIME_FORMAT,
  rankPrefix,
  resolveZone,
  safeFilePart,
} from "./auctionReportData";
import { notBeatingStartPriceLabel } from "../../../../packages/startPriceText";

// The "Download Report" Excel, one sheet per part:
//   Summary, Lot results, Bidders, Bid matrix, Positions, Ranking.
// Every lead, rank and total comes from the backend auction-result service
// (GET /events/:eventId/report), the same ranking used by the other reports.
// Prebid and post-bid columns appear only when the auction has them.

const STAGE = { PREBID: "Prebid", AUCTION: "Auction", POST_BID: "Post-bid" };
const LABEL_STYLE = { font: { bold: true } };
const SUBTITLE_STYLE = { font: { italic: true } };

const personName = (person) =>
  `${person?.firstName || ""} ${person?.lastName || ""}`.trim() || "–";

const createdByText = (person) => {
  if (!person) return "–";
  const name = personName(person);
  return person.email ? `${name} (${person.email})` : name;
};

const percentCell = (value) =>
  value === null || value === undefined
    ? textCell("")
    : { t: "n", v: value / 100, z: "0.00%", s: { numFmt: "0.00%" } };

const label = (text) => withStyle(textCell(text), LABEL_STYLE);

// amounts written inside text, e.g. "B – 1,100.00 – H2", in the same format
// as the amount cells
const amountFormat = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const amountText = (value) => amountFormat.format(Number(value));

// Builds one sheet: two title lines, then a table with filter buttons.
//   columns: [{ label, width, cell: (row) => cell }]
const tableSheet = ({ titleLines, columns, rows, totalRow }) => {
  const headerRowIndex = titleLines.length + 1;
  const aoa = [
    ...titleLines,
    [],
    columns.map((column) =>
      withStyle(textCell(column.label), TABLE_HEADER_STYLE)
    ),
    ...rows.map((row) => columns.map((column) => column.cell(row))),
  ];
  if (totalRow) {
    aoa.push(
      columns.map((column) =>
        column.total ? withStyle(column.total(), TOTAL_STYLE) : textCell("")
      )
    );
  }

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  worksheet["!cols"] = columns.map(({ width }) => ({ wch: width }));
  const lastRow = headerRowIndex + rows.length;
  worksheet["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { r: headerRowIndex, c: 0 },
      e: { r: lastRow, c: columns.length - 1 },
    }),
  };
  return worksheet;
};

export const exportAuctionReportToXLSX = ({ report, zone }) => {
  const { auction, summary, lots, bidders } = report;
  const timeZone = resolveZone(zone);
  const now = moment().tz(timeZone);
  const zoneAbbr = now.format("z");
  const currency = auction.currency;
  const rank = rankPrefix(auction.eventType);
  const stepName =
    auction.eventType === "FORWARD_AUCTION" ? "Increment" : "Decrement";
  const typeText = `${auctionTypeText(auction.eventType)} – ${auctionFormatText(
    auction.eventFormat
  )}`;

  // stage columns and labels only when the auction used them
  const hasPrebid = lots.some((lot) => lot.bidCountByStage[STAGE.PREBID] > 0);
  const hasPostBid = lots.some(
    (lot) => lot.bidCountByStage[STAGE.POST_BID] > 0
  );
  const showStage = hasPrebid || hasPostBid;
  const onlyIf = (condition, items) => (condition ? items : []);

  const date = (value) => dateCell(value, timeZone, auction.eventFormat);
  const bidderById = new Map(bidders.map((bidder) => [bidder.userId, bidder]));
  const nameOf = (userId) => personName(bidderById.get(userId));
  const description = (lot) => lot.longDescription || lot.shortDescription;
  // "YES (Consider the start price)": prebids that don't beat the start price
  // are still ranked here, with a label
  const stageText = (offer) =>
    offer.belowStartPrice
      ? `${offer.stage}, ${notBeatingStartPriceLabel(
          auction.eventType
        ).toLowerCase()}`
      : offer.stage;
  const leadName = (lot) => {
    if (!lot.lead) return "No bid";
    const name = nameOf(lot.lead.userId);
    return showStage ? `${name} (${stageText(lot.lead)})` : name;
  };

  const titleLines = [
    [withStyle(textCell(auction.title), TITLE_STYLE)],
    [
      withStyle(
        textCell(
          `Auction ID ${auction.eventNumber} · ${typeText} · All times in ${zoneAbbr}`
        ),
        SUBTITLE_STYLE
      ),
    ],
  ];

  // 1. Summary: the Lots Listing header layout, plus the values
  const auctionDescription =
    auction.description &&
    auction.description.trim() !== (auction.title || "").trim()
      ? auction.description
      : null;
  const valueItems = [
    ["Start value", summary.startValue],
    ...onlyIf(hasPrebid, [["Best prebid value", summary.prebidValue]]),
    ["Lead value", summary.leadValue],
    ...onlyIf(hasPostBid, [
      ["of which post-bid", summary.leadValueByStage[STAGE.POST_BID]],
    ]),
  ];
  const summaryRows = [
    [
      label("Lot Owner"),
      withStyle(textCell(auction.ownerOfLots || "–"), TITLE_STYLE),
    ],
    [
      label("Auction:"),
      withStyle(textCell(auction.title), { font: { bold: true, sz: 12 } }),
    ],
    ...onlyIf(auctionDescription, [
      [label("Description:"), textCell(auctionDescription)],
    ]),
    [],
    [
      `Auction Type & ID`,
      `Start Date & Time (${zoneAbbr})`,
      `End Date & Time (${zoneAbbr})`,
      null,
      "",
      "Total",
      "Bid",
      "No bid",
    ].map((text) =>
      text === null
        ? textCell("")
        : withStyle(textCell(text), TABLE_HEADER_STYLE)
    ),
    [
      textCell(typeText),
      date(auction.startAt),
      date(auction.endAt),
      textCell(""),
      textCell("Lots"),
      numberCell(summary.lots),
      numberCell(summary.lotsWithOffer),
      numberCell(summary.lotsNoOffer),
    ],
    [
      textCell(auction.eventNumber),
      textCell(""),
      textCell(""),
      textCell(""),
      textCell("Invited bidders"),
      numberCell(summary.invitedBidders),
      numberCell(summary.biddersWithOffer),
      // post-bidders who were never invited are not "invited, no bid"
      numberCell(
        Math.max(0, summary.invitedBidders - summary.biddersWithOffer)
      ),
    ],
    [],
    valueItems.map(([text]) =>
      withStyle(textCell(`${text} (${currency})`), TABLE_HEADER_STYLE)
    ),
    valueItems.map(([, value]) => amountCell(value)),
    [],
    [label("Conducted by"), textCell(auction.companyName || "–")],
    [label("Published"), date(auction.publishAt)],
    [label("First lot started"), date(auction.firstLotStartAt)],
    [label("Last lot ended"), date(auction.lastLotEndAt)],
    ...onlyIf(auction.message, [[label("Message"), textCell(auction.message)]]),
    [],
    [
      textCell(`Generated: ${now.format(DATE_TIME_FORMAT)} ${zoneAbbr}`),
      withStyle(textCell("Powered by V3bid.com"), FOOTER_STYLE),
      textCell(""),
      textCell(`Created by: ${createdByText(auction.auctioneer)}`),
    ],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
  summarySheet["!cols"] = [26, 26, 22, 3, 22, 10, 10, 10].map((wch) => ({
    wch,
  }));
  // the start and end dates sit next to both summary rows, as in the draft
  const typeRow = summaryRows.findIndex(
    (row) => row[0]?.v === "Auction Type & ID"
  );
  summarySheet["!merges"] = [1, 2].map((c) => ({
    s: { r: typeRow + 1, c },
    e: { r: typeRow + 2, c },
  }));

  // 2. Lot results
  const offerAt = (lot, position) => lot.offers[position - 1];
  const lotResultsSheet = tableSheet({
    titleLines,
    rows: lots,
    totalRow: true,
    columns: [
      { label: "Lot No", width: 8, cell: (lot) => textCell(lot.lotNumber) },
      {
        label: "Description",
        width: 34,
        cell: (lot) => withStyle(textCell(description(lot)), WRAP_STYLE),
        total: () => textCell("Total"),
      },
      {
        label: `Start Date & Time (${zoneAbbr})`,
        width: 21,
        cell: (lot) => date(lot.startDateAndTime),
      },
      {
        label: `End Date & Time (${zoneAbbr})`,
        width: 21,
        cell: (lot) => date(lot.endDateAndTime),
      },
      { label: "Unit", width: 8, cell: (lot) => textCell(lot.unit) },
      { label: "Qty", width: 10, cell: (lot) => numberCell(lot.quantity) },
      {
        label: `Start Price (${currency})`,
        width: 14,
        cell: (lot) => amountCell(lot.startPrice),
      },
      {
        label: `${stepName} (${currency})`,
        width: 12,
        cell: (lot) => numberCell(lot.increment),
      },
      {
        label: "% Gain wrt Start Price",
        width: 12,
        cell: (lot) =>
          lot.lead ? percentCell(lot.lead.gainPercent) : textCell("–"),
      },
      {
        label: `Lead Bidder (${rank}1)`,
        width: 22,
        cell: (lot) => textCell(lot.lead ? nameOf(lot.lead.userId) : "No bid"),
      },
      ...onlyIf(showStage, [
        {
          label: "Lead Stage",
          width: 10,
          cell: (lot) => textCell(lot.lead ? stageText(lot.lead) : ""),
        },
      ]),
      {
        label: `Lead Rate (${currency})`,
        width: 14,
        cell: (lot) => (lot.lead ? amountCell(lot.lead.amount) : textCell("–")),
      },
      {
        label: `Lead Value (${currency})`,
        width: 16,
        cell: (lot) => (lot.lead ? amountCell(lot.lead.value) : textCell("–")),
        total: () => amountCell(summary.leadValue),
      },
      {
        label: `${rank}2 Bid (${currency})`,
        width: 13,
        cell: (lot) =>
          offerAt(lot, 2) ? amountCell(offerAt(lot, 2).amount) : textCell("–"),
      },
      {
        label: `${rank}3 Bid (${currency})`,
        width: 13,
        cell: (lot) =>
          offerAt(lot, 3) ? amountCell(offerAt(lot, 3).amount) : textCell("–"),
      },
      {
        label: "No. of Bids",
        width: 9,
        cell: (lot) => numberCell(lot.bidCount),
      },
      {
        label: "Bidders bid",
        width: 9,
        cell: (lot) => numberCell(lot.biddersWithOffer),
      },
      {
        label: "Invited bidders",
        width: 9,
        cell: (lot) => numberCell(lot.invitedBidders),
      },
    ],
  });

  // 3. Bidders
  const { rankTotals } = summary;
  const biddersSheet = tableSheet({
    titleLines,
    rows: bidders.map((bidder, index) => ({ ...bidder, index })),
    totalRow: true,
    columns: [
      {
        label: "Bidder",
        width: 24,
        cell: (b) => textCell(`${b.index + 1}. ${personName(b)}`),
        total: () => textCell(`Total bidders: ${bidders.length}`),
      },
      { label: "Mail ID", width: 30, cell: (b) => textCell(b.email) },
      { label: "Phone No", width: 14, cell: (b) => textCell(b.contactNumber) },
      {
        label: "Alternate Phone No",
        width: 14,
        cell: (b) => textCell(b.alternateContactNumber),
      },
      {
        label: "Allotted Lots",
        width: 9,
        cell: (b) => numberCell(b.lotsAllotted),
        total: () => numberCell(summary.lotsAllotted),
      },
      {
        label: "Lots bid",
        width: 9,
        cell: (b) => numberCell(b.lotsWithOffer),
        total: () => numberCell(summary.lotsWithOffer),
      },
      {
        label: `${rank}1 Lots`,
        width: 9,
        cell: (b) => numberCell(b.rank1.count),
        total: () => numberCell(rankTotals.rank1.count),
      },
      {
        label: `${rank}1 Value (${currency})`,
        width: 16,
        cell: (b) => amountCell(b.rank1.value),
        total: () => amountCell(rankTotals.rank1.value),
      },
      ...onlyIf(hasPostBid, [
        {
          label: `of which post-bid ${rank}1`,
          width: 10,
          cell: (b) => numberCell(b.rank1.postBidCount),
          total: () => numberCell(summary.lotsByLeadStage[STAGE.POST_BID]),
        },
      ]),
      {
        label: `${rank}2 Lots`,
        width: 9,
        cell: (b) => numberCell(b.rank2.count),
        total: () => numberCell(rankTotals.rank2.count),
      },
      {
        label: `${rank}2 Value (${currency})`,
        width: 16,
        cell: (b) => amountCell(b.rank2.value),
        total: () => amountCell(rankTotals.rank2.value),
      },
      {
        label: "Other Lots",
        width: 9,
        cell: (b) => numberCell(b.other.count),
        total: () => numberCell(rankTotals.other.count),
      },
      {
        label: `Other Value (${currency})`,
        width: 16,
        cell: (b) => amountCell(b.other.value),
        total: () => amountCell(rankTotals.other.value),
      },
      {
        label: "% of Lead Value",
        width: 10,
        cell: (b) => percentCell(b.leadValuePercent),
        total: () => percentCell(summary.leadValue ? 100 : null),
      },
    ],
  });

  // 4. Bid matrix: every bidder's best offer on every lot
  const bidMatrixSheet = tableSheet({
    titleLines,
    rows: lots,
    columns: [
      { label: "Lot No", width: 8, cell: (lot) => textCell(lot.lotNumber) },
      {
        label: "Description",
        width: 30,
        cell: (lot) => withStyle(textCell(description(lot)), WRAP_STYLE),
      },
      { label: "Unit", width: 8, cell: (lot) => textCell(lot.unit) },
      { label: "Qty", width: 10, cell: (lot) => numberCell(lot.quantity) },
      {
        label: `Start Price (${currency})`,
        width: 14,
        cell: (lot) => amountCell(lot.startPrice),
      },
      {
        label: "Bidders bid",
        width: 9,
        cell: (lot) => numberCell(lot.biddersWithOffer),
      },
      {
        label: `Lead Bid (${currency})`,
        width: 13,
        cell: (lot) => (lot.lead ? amountCell(lot.lead.amount) : textCell("–")),
      },
      {
        label: `Lead Value (${currency})`,
        width: 16,
        cell: (lot) => (lot.lead ? amountCell(lot.lead.value) : textCell("–")),
      },
      {
        label: `Lead Bidder (${rank}1)`,
        width: 24,
        cell: (lot) => textCell(leadName(lot)),
      },
      ...bidders.map((bidder, index) => ({
        label: `${index + 1}. ${personName(bidder)}`,
        width: 16,
        cell: (lot) => {
          const offer = lot.offers.find(
            ({ userId }) => userId === bidder.userId
          );
          if (!offer) return textCell("–");
          return offer.stage === STAGE.AUCTION && !offer.belowStartPrice
            ? amountCell(offer.amount)
            : textCell(`${amountText(offer.amount)} (${stageText(offer)})`);
        },
      })),
    ],
  });

  // 5. Positions: each bidder's place on every lot they bid on
  const positions = bidders.flatMap((bidder) =>
    lots
      .map((lot) => ({
        bidder,
        lot,
        offer: lot.offers.find(({ userId }) => userId === bidder.userId),
      }))
      .filter(({ offer }) => offer)
  );
  const positionsSheet = tableSheet({
    titleLines,
    rows: positions,
    columns: [
      {
        label: "Bidder",
        width: 22,
        cell: ({ bidder }) => textCell(personName(bidder)),
      },
      { label: "Lot No", width: 8, cell: ({ lot }) => textCell(lot.lotNumber) },
      {
        label: "Description",
        width: 30,
        cell: ({ lot }) =>
          withStyle(textCell(lot.shortDescription), WRAP_STYLE),
      },
      {
        label: "Quantity & Unit",
        width: 14,
        cell: ({ lot }) => textCell(`${lot.quantity} ${lot.unit || ""}`.trim()),
      },
      {
        label: `Best Offer (${currency})`,
        width: 14,
        cell: ({ offer }) => amountCell(offer.amount),
      },
      ...onlyIf(showStage, [
        {
          label: "Stage",
          width: 10,
          cell: ({ offer }) => textCell(stageText(offer)),
        },
      ]),
      {
        label: "Position",
        width: 9,
        cell: ({ offer }) => textCell(`${rank}${offer.rank}`),
      },
      {
        label: "% Gain wrt Start Price",
        width: 12,
        cell: ({ offer }) => percentCell(offer.gainPercent),
      },
      {
        label: `Start Price (${currency})`,
        width: 14,
        cell: ({ lot }) => amountCell(lot.startPrice),
      },
      {
        label: stepName,
        width: 10,
        cell: ({ lot }) => numberCell(lot.increment),
      },
      {
        label: "Bidder's Bids",
        width: 9,
        cell: ({ offer }) => numberCell(offer.bidCount),
      },
      {
        label: "Bidders bid",
        width: 9,
        cell: ({ lot }) => numberCell(lot.biddersWithOffer),
      },
      {
        label: "Invited bidders",
        width: 9,
        cell: ({ lot }) => numberCell(lot.invitedBidders),
      },
    ],
  });

  // 6. Ranking: best value per stage, the lead, then everyone else in order
  const stageValue = (stage) => (lot) => {
    const amount = lot.bestAmountByStage[stage];
    return amount === null ? textCell("–") : amountCell(amount);
  };
  const rankingSheet = tableSheet({
    titleLines,
    rows: lots,
    columns: [
      { label: "Lot No", width: 8, cell: (lot) => textCell(lot.lotNumber) },
      {
        label: "Description",
        width: 30,
        cell: (lot) => withStyle(textCell(description(lot)), WRAP_STYLE),
      },
      ...onlyIf(hasPrebid, [
        {
          label: `Best Prebid (${currency})`,
          width: 14,
          cell: stageValue(STAGE.PREBID),
        },
      ]),
      {
        label: `Best Auction Bid (${currency})`,
        width: 14,
        cell: stageValue(STAGE.AUCTION),
      },
      ...onlyIf(hasPostBid, [
        {
          label: `Best Post-bid (${currency})`,
          width: 14,
          cell: stageValue(STAGE.POST_BID),
        },
      ]),
      {
        label: `Lead Bid (${currency})`,
        width: 13,
        cell: (lot) => (lot.lead ? amountCell(lot.lead.amount) : textCell("–")),
      },
      {
        label: `Lead Bidder (${rank}1)`,
        width: 24,
        cell: (lot) => textCell(leadName(lot)),
      },
      {
        label: "Other Bidders",
        width: 40,
        cell: (lot) =>
          withStyle(
            textCell(
              lot.offers
                .slice(1)
                .map((offer) => {
                  const text = `${nameOf(offer.userId)} – ${amountText(
                    offer.amount
                  )} – ${rank}${offer.rank}`;
                  return showStage ? `${text} (${stageText(offer)})` : text;
                })
                .join("\n")
            ),
            WRAP_STYLE
          ),
      },
    ],
  });

  const workbook = XLSX.utils.book_new();
  [
    [summarySheet, "Summary"],
    [lotResultsSheet, "Lot results"],
    [biddersSheet, "Bidders"],
    [bidMatrixSheet, "Bid matrix"],
    [positionsSheet, "Positions"],
    [rankingSheet, "Ranking"],
  ].forEach(([sheet, name]) =>
    XLSX.utils.book_append_sheet(workbook, sheet, name)
  );

  XLSX.writeFile(
    workbook,
    `auction-${safeFilePart(auction.eventNumber)}-report.xlsx`
  );
};
